import React, { useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import {
  getMxBookingBounds,
  parseYmdAsLocalNoon,
  dateToYmd,
} from "../../utils/bookingWindowMx";

import "react-day-picker/style.css";

export function BookingDateRangeHint({ className = "" }) {
  const { minStr, maxStr } = useMemo(() => getMxBookingBounds(), []);

  return (
    <p className={`text-xs text-brand-gray leading-relaxed ${className}`}>
      Primera fecha posible: <span className="font-medium text-brand-dark">mañana</span> (no el mismo día).
      Rango:{" "}
      <span className="font-medium text-brand-dark">
        {minStr} — {maxStr}
      </span>{" "}
      (CDMX)
    </p>
  );
}

/**
 * Mes completo navegable; solo días hábiles entre mañana y +7 días (CDMX). No incluye hoy.
 */
export default function BookingMonthPicker({ valueYmd, onChangeYmd, className = "" }) {
  const { minDate, maxDate } = useMemo(
    () => getMxBookingBounds(),
    []
  );

  const selected = valueYmd ? parseYmdAsLocalNoon(valueYmd) : undefined;

  const disabledMatchers = useMemo(
    () => [
      { before: minDate },
      { after: maxDate },
      { dayOfWeek: [0, 6] },
    ],
    [minDate, maxDate]
  );

  return (
    <div className={`booking-month-picker rounded-2xl border border-brand-brown/15 bg-white p-2.5 sm:p-5 shadow-sm min-w-0 ${className}`}>
      <DayPicker
        mode="single"
        locale={es}
        weekStartsOn={1}
        selected={selected}
        onSelect={(d) => onChangeYmd(d ? dateToYmd(d) : "")}
        disabled={disabledMatchers}
        defaultMonth={minDate}
        className="mx-auto"
        modifiersClassNames={{
          selected: "!bg-brand-lavender !text-white !font-semibold hover:!bg-brand-lavender",
          today: "!font-bold !text-brand-lavender",
        }}
        classNames={{
          months: "flex flex-col gap-4",
          month: "space-y-3",
          month_caption:
            "flex justify-center items-center pt-1 pb-2 font-sans text-base sm:text-lg text-brand-dark font-semibold capitalize",
          nav: "flex items-center justify-between gap-1 sm:gap-2 px-0.5 sm:px-1 -mt-9 sm:-mt-10 mb-2 relative z-10",
          button_previous:
            "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-brand-brown/20 text-brand-dark hover:bg-brand-lavender/10",
          button_next:
            "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-brand-brown/20 text-brand-dark hover:bg-brand-lavender/10",
          weekdays: "grid grid-cols-7 gap-0.5 sm:gap-1 text-center",
          weekday:
            "text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-brand-gray py-1",
          week: "grid grid-cols-7 gap-0.5 sm:gap-1 mt-1",
          day: "flex justify-center p-0",
          day_button:
            "h-8 w-8 sm:h-10 sm:w-10 rounded-full text-xs sm:text-sm font-medium text-brand-dark hover:bg-brand-lavender/15 transition-colors",
          disabled: "opacity-35 cursor-not-allowed",
          outside: "opacity-45 text-brand-gray",
        }}
      />
      <p className="text-xs text-brand-gray text-center mt-4 leading-relaxed px-1">
        El día de hoy no se puede elegir. Los días atenuados son fines de semana o quedan fuera del
        período permitido.
      </p>
    </div>
  );
}
